import { useState } from "react";
import { Link } from "react-router-dom";
import { LucideClock, LucideArrowRight } from "lucide-react";
import { pdfjs } from "react-pdf";
import { PDFPreview } from "@/reports/report_components/PDFPreview";
import { ReportFormHeader } from "@/reports/report_components/ReportFormHeader";
import { REPORT_TYPES } from "@/constants/reportTypes";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const ENABLED = REPORT_TYPES.filter((t) => !t.disabled);

export default function FreeTrialPicker() {
  const [selected, setSelected] = useState(ENABLED[0] ?? null);

  return (
    <div className="flex flex-col h-[calc(100dvh-60px)]">
      <ReportFormHeader
        titleOnly
        title="Free Trial"
        subtitle="Choose a report card, then try it for free - no account needed."
      />

      <div className="flex flex-1 overflow-hidden">
        {/* ── LEFT - card list ──────────────────────────────── */}
        <div className="w-1/2 shrink-0 overflow-y-auto border-r border-border px-4 py-5 flex flex-col gap-2.5">
          {REPORT_TYPES.map((type) => {
            const isSelected = selected?.value === type.value;
            return (
              <button
                key={type.value}
                type="button"
                disabled={type.disabled}
                onClick={() => !type.disabled && setSelected(type)}
                className={`w-full text-left border rounded-[0.875rem] px-4 py-4 bg-card transition-[border-color,box-shadow] duration-150 ${
                  type.disabled
                    ? "opacity-50 cursor-not-allowed"
                    : isSelected
                    ? "border-brand-500 shadow-[0_0_0_1px_hsl(var(--brand-500))]"
                    : "border-border hover:border-muted-foreground/40 hover:shadow-[0_2px_8px_hsl(var(--foreground)/0.05)] cursor-pointer"
                }`}
              >
                {/* Card header row */}
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[0.9375rem] font-medium text-foreground leading-[1.3]">{type.label}</span>
                  <span className="text-xs text-muted-foreground bg-muted rounded-[4px] px-[0.4rem] py-[0.125rem] leading-[1.4]">{type.subtitle}</span>
                  {type.disabled && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/70 bg-muted/80 rounded-[4px] px-[0.4rem] py-[0.125rem] leading-[1.4]">
                      <LucideClock size={10} />
                      Coming soon
                    </span>
                  )}
                </div>

                <p className="text-[0.8125rem] text-muted-foreground leading-[1.55] m-0 mb-3">{type.description}</p>

                {/* CTA */}
                {type.disabled ? (
                  <span className="inline-flex items-center gap-[0.375rem] text-sm font-medium text-primary-foreground bg-primary rounded-[0.625rem] px-4 py-2 opacity-40 cursor-not-allowed">
                    Try this report
                    <LucideArrowRight size={14} />
                  </span>
                ) : (
                  <Link
                    to={type.freeTrialPath}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="inline-flex items-center gap-[0.375rem] text-sm font-medium text-primary-foreground bg-primary rounded-[0.625rem] px-4 py-2 transition-[background,transform] duration-150 hover:bg-primary-hover hover:-translate-y-px">
                      Try this report
                      <LucideArrowRight size={14} />
                    </span>
                  </Link>
                )}
              </button>
            );
          })}
        </div>

        {/* ── RIGHT - PDF preview ───────────────────────────── */}
        <div className="flex-1 overflow-hidden">
          <PDFPreview
            src={selected?.pdfPreview ?? null}
            defaultPageCount={2}
          />
        </div>
      </div>
    </div>
  );
}
